// Global Instructions Rule Applied!
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../../state_manager/utils/reportingGridExcelExport.ts'
import { fetchAllDealsForExport } from '../../../shared/table_grid_export/fetchAllFilteredForExport.ts'

import DealsTable from './DealsTable.tsx'
import { useDealsDrawer } from '../../hooks/useDealsDrawer.ts'
import { useDealsData } from '../../hooks/useDealsData.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { useDealManager } from '../../hooks/useDealManager.ts'
import { DealFields } from '../../../../state_manager/data_models/deal_record.ts'
import { MENU_ITEM_KEY_DEALS, TABLE_DEALS } from '../../../../state_manager/globals.ts'

import ViewToolbar from '../../../../core/components/ViewToolbar/index.tsx'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import { ViewConfig } from '../../../../core/components/ViewConfig/index.ts'
import { useDealsReportId } from '../../../../state_manager/hooks/useDealsReportId.ts'
import { useReportingGridColumnsWithPreferences } from '../../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../../state_manager/utils/reportingColumnCatalog.ts'
import { useMenuItemUserPreference } from '../../../global_configs/useMenuItemUserPreference.ts'
import { useStageMoveRights } from '../../../../core/hooks/useStageMoveRights.ts'

export default function DealsPage() {
  // Lookup, currency, and companies functionality
  const { getLookupLabel, getLookupOptions } = useLookups()
  const { formatAmountById } = useCurrencies()
  const { defaultPipelineId } = useDealManager()

  // Get pipeline configs and stages from Redux state
  const pipelineConfigs = useSelector((state: any) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: any) => state.globalConfigs?.pipeline_stages || [])

  const dealsReportId = useDealsReportId()
  const { preferenceRow: dealsPreferenceRowForFilterKeys } = useMenuItemUserPreference(MENU_ITEM_KEY_DEALS)
  const dealsGridColumnsForFilterKeys = useReportingGridColumnsWithPreferences(
    dealsReportId,
    dealsPreferenceRowForFilterKeys
  )
  const dealsFilterOptionKeys = useMemo(
    () =>
      dealsGridColumnsForFilterKeys
        .filter((c) => c.filterable !== false)
        .map((c) => String(c.filterKey || c.valueKey || ''))
        .filter((k) => k.length > 0),
    [dealsGridColumnsForFilterKeys]
  )

  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  // Data management hook v2 (includes user preference row for column layout)
  const {
    dealsFilterOptions,
    dealsData,
    dealsDataLoading,
    dealsFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    handleFetchFilterOptions,
    handleFetchDeals,
    handleSetFilters,
    handleSetSorting,
    defaultTableFilters,
    defaultTableSorting,
    handleResetFilters
  } = useDealsData(dealsFilterOptionKeys)

  const dealsGridColumns = useReportingGridColumnsWithPreferences(dealsReportId, preferenceRow)

  const viewConfigColumns = dealsGridColumns

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        dealsReportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'deals' }
      ),
    [dealsReportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!dealsReportId) return null
    const rows = getReportingGridColumnsForReportId(dealsReportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [dealsReportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback((valueKey: string) => columnByValueKey.get(valueKey), [columnByValueKey])

  const dealsColumnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  // Drawer management hook
  const { handleDealNameClick, handleTasksClick, handleTaskToggle } = useDealsDrawer(dealsData)

  // Get pipeline stage options filtered by pipeline ID
  // Used for cell editing in the table (deal-specific options)
  const getPipelineStageOptionsForPipeline = useCallback(
    (pipelineId: string | null): any[] => {
      if (!pipelineId || !pipelineConfigs.length || !pipelineStages.length) {
        return []
      }

      // Filter configs by pipeline ID and visible_tbl = true
      const filteredConfigs = pipelineConfigs.filter(
        (config: any) => config.pipeline === pipelineId && config.visible_tbl === true
      )

      // Map to options: { value: config.id, label: stage.name, sequence: config.sequence_tbl }
      const allPipelineStageOptions = filteredConfigs
        .map((config: any) => {
          const stage = pipelineStages.find((s: any) => s.id === config.stage)
          return {
            value: config.id,
            label: stage?.name || '',
            sequence: config.sequence_tbl || config.sequence_kb || 0
          }
        })
        .filter((option: any) => option.label)
        .sort((a: any, b: any) => a.label.localeCompare(b.label))

      return allPipelineStageOptions
    },
    [pipelineConfigs, pipelineStages]
  )

  // Get pipeline stage options for a specific deal
  // Uses deal's pipeline_origination or falls back to default pipeline.
  // Stages the user may not move this deal into or out of are disabled with the
  // reason (the grid's inline edit; the filter dropdown below is unaffected).
  const applyStageMoveRights = useStageMoveRights()
  const getPipelineStageOptionsForDeal = useCallback(
    (deal: any): any[] => {
      const dealPipelineId = deal?.[DealFields.deals__pipeline_origination] || defaultPipelineId
      return applyStageMoveRights(getPipelineStageOptionsForPipeline(dealPipelineId), deal?.[DealFields.pipeline_deals__stage])
    },
    [getPipelineStageOptionsForPipeline, defaultPipelineId, applyStageMoveRights]
  )

  // Filtered pipeline stage options function
  // Used for filter dropdown (shows all options, not deal-specific)
  // Filters by visible_tbl = true from pipeline_configs for default pipeline
  const getFilteredPipelineStageOptionsForDeals = useCallback((): any[] => {
    if (!defaultPipelineId || !pipelineConfigs.length || !pipelineStages.length) {
      return []
    }

    // Filter configs by default pipeline ID and visible_tbl = true
    const filteredConfigs = pipelineConfigs.filter(
      (config: any) => config.pipeline === defaultPipelineId && config.visible_tbl === true
    )

    // Map to options: { value: config.id, label: stage.name, sequence: config.sequence_tbl }
    const options = filteredConfigs
      .map((config: any) => {
        const stage = pipelineStages.find((s: any) => s.id === config.stage)
        return {
          value: config.id,
          label: stage?.name || '',
          sequence: config.sequence_tbl || config.sequence_kb || 0
        }
      })
      .filter((option: any) => option.label)
      .sort((a: any, b: any) => a.label.localeCompare(b.label))

    return options
  }, [defaultPipelineId, pipelineConfigs, pipelineStages])

  const [exportLoading, setExportLoading] = useState(false)

  const handleExportTable = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data, error } = await fetchAllDealsForExport(activeSorting, activeFilters, token, dealsReportId)
      if (error) {
        showErrorToast(error)
        return
      }
      if (data.length === 0) {
        toast.info('No rows to export')
        return
      }
      const records = data.map((r) => ({ ...r }) as Record<string, unknown>)
      const payload = buildReportingGridExcelPayload(dealsGridColumns, records)
      const filename = `Deals - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [activeSorting, activeFilters, dealsReportId, dealsGridColumns])

  return (
    <>
      <div className='h-full overflow-hidden'>
        <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
          <ViewToolbar
            moduleKey={TABLE_DEALS}
            recordTitleField={DealFields.deals__name}
            activeFilters={activeFilters}
            currentFilterOptions={dealsFilterOptions}
            filterOptionsLoading={dealsFilterOptionsLoading}
            onRefreshFilterOptions={handleFetchFilterOptions}
            onRefreshData={(activeFilters: any) => handleFetchDeals(activeSorting, activeFilters)}
            onFilterChange={handleSetFilters}
            defaultFilters={defaultTableFilters}
            onResetFilters={handleResetFilters}
            exportEnabled
            onExport={handleExportTable}
            exportLoading={exportLoading}
            beforeAddSlot={
              <ViewConfig
                moduleTitle='Deals'
                columns={viewConfigColumns}
                filterOptions={dealsFilterOptions}
                filterOptionsLoading={dealsFilterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                columnLayout={dealsColumnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: defaultTableFilters,
                  onApplyFilters: async (filters) => {
                    handleSetFilters(filters)
                  },
                  onResetToDefaults: async () => {
                    handleSetFilters(defaultTableFilters)
                  }
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => {
                    handleSetSorting(sorting)
                  },
                  onResetToDefaults: async () => {
                    handleSetSorting(defaultTableSorting)
                  }
                }}
              />
            }
          />

          {/* Deals Table */}
          <div className='flex-1 min-h-0 h-full flex flex-col'>
            <DealsTable
              gridColumns={dealsGridColumns}
              loading={dealsDataLoading}
              loadingMore={false} // TODO: Implement loading more
              hasMore={false} // TODO: Implement has more
              currentPage={1} // TODO: Implement current page
              activeFilters={activeFilters}
              activeSorting={activeSorting}
              onLoadMore={() => {}} // TODO: Implement load more
              onFiltersChange={(filters) => {
                // Transform filters from { key: value } format to FilterValue[] format
                const filterArray = Object.entries(filters).map(([key, value]) => ({
                  key,
                  value
                }))
                handleSetFilters(filterArray)
              }}
              onSortChange={(sortConfig: any) => {
                if (sortConfig) {
                  // Check if this is the default sort (pipeline_stage asc) - treat as no sorting
                  if (sortConfig.column === 'pipeline_stage' && sortConfig.direction === 'asc') {
                    handleSetSorting([])
                  } else {
                    handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
                  }
                } else {
                  handleSetSorting([])
                }
              }}
              onDealNameClick={handleDealNameClick}
              onTasksClick={handleTasksClick}
              onTaskToggle={handleTaskToggle}
              filterOptions={dealsFilterOptions}
              filterOptionsLoading={dealsFilterOptionsLoading}
              getLookupLabel={getLookupLabel}
              formatAmountById={formatAmountById}
              getPipelineStageOptions={getFilteredPipelineStageOptionsForDeals}
              getPipelineStageOptionsForDeal={getPipelineStageOptionsForDeal}
              getLookupOptions={getLookupOptions}
            />
          </div>
        </div>
      </div>
    </>
  )
}
